import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatEUR } from "@/lib/api";
import { Target, CurrencyEur, Bank, Wallet, TrendUp } from "@phosphor-icons/react";
import { toast } from "sonner";

const FIELDS = [
  { key: "annual_signed_dossiers", label: "Obj. annuel — signés", suffix: "dossiers", icon: Target, color: "var(--cf-primary)" },
  { key: "annual_ca_net", label: "Obj. annuel — CA net", suffix: "€", icon: CurrencyEur, color: "var(--cf-success)" },
  { key: "monthly_ca_net", label: "Obj. mensuel — CA net", suffix: "€", icon: TrendUp, color: "var(--cf-success)" },
  { key: "monthly_contacts", label: "Obj. mensuel — contacts", suffix: "contacts", icon: Target, color: "var(--cf-warning)" },
  { key: "monthly_mandates", label: "Obj. mensuel — mandats", suffix: "mandats", icon: Target, color: "var(--cf-warning)" },
  { key: "monthly_fixed_charges", label: "Charges fixes / mois", suffix: "€", icon: Bank, color: "var(--cf-danger)" },
  { key: "current_treasury", label: "Trésorerie actuelle", suffix: "€", icon: Wallet, color: "var(--cf-primary)" },
];

export default function Objectifs() {
  const [goals, setGoals] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/goals").then((r) => { setGoals(r.data); setForm(r.data); });
  }, []);

  const dirty = goals && Object.keys(form).some((k) => Number(form[k]) !== Number(goals[k]));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach(({ key }) => {
        if (form[key] !== undefined) payload[key] = Number(form[key]);
      });
      const res = await api.patch("/goals", payload);
      setGoals(res.data);
      setForm(res.data);
      toast.success("Objectifs enregistrés");
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  if (!goals) return <Layout title="Objectifs"><div className="card-base h-60 animate-pulse" /></Layout>;

  // Computed derived
  const dailyC = Math.round((form.monthly_contacts || 0) / 22);
  const dailyM = Math.round((form.monthly_mandates || 0) / 22);
  const treasuryEnd = (Number(form.current_treasury) || 0) + (Number(form.monthly_ca_net) || 0) - (Number(form.monthly_fixed_charges) || 0);

  return (
    <Layout title="Objectifs" centerTitle>
      <button onClick={() => navigate("/")} className="hidden md:inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-5 font-semibold">← Retour</button>
      <div className="hidden md:block mb-5">
        <div className="overline">Paramètres</div>
        <h1 className="font-display text-4xl font-extrabold text-slate-900">Objectifs</h1>
        <p className="text-slate-500 mt-1">Pilote tes cibles annuelles, mensuelles et tes charges.</p>
      </div>

      <div className="space-y-3" data-testid="goals-form">
        {FIELDS.map(({ key, label, suffix, icon: Icon, color }) => (
          <div key={key} className="card-base p-3 md:p-4 flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `color-mix(in srgb, ${color} 12%, white)` }}>
              <Icon size={18} color={color} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] md:text-[13px] font-semibold text-slate-800 leading-tight">{label}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">en {suffix}</div>
            </div>
            <input
              type="number"
              value={form[key] ?? ""}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              className="cf-input w-24 md:w-40 text-right font-bold tabular-nums py-2 flex-shrink-0"
              data-testid={`goal-${key}`}
            />
          </div>
        ))}
      </div>

      <div className="card-tinted-blue p-5 mt-5" data-testid="derived-info">
        <div className="overline mb-2" style={{ color: "var(--cf-primary-hover)" }}>Dérivés automatiques</div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><div className="text-slate-500">Contacts / jour</div><div className="metric-value text-xl text-[color:var(--cf-primary)]">{dailyC}</div></div>
          <div><div className="text-slate-500">Mandats / jour</div><div className="metric-value text-xl text-[color:var(--cf-primary)]">{dailyM}</div></div>
          <div className="col-span-2 border-t border-[color:var(--cf-primary-border)] pt-3">
            <div className="text-slate-500">Trésorerie projetée fin de mois</div>
            <div className="metric-value text-2xl" style={{ color: treasuryEnd < 0 ? "var(--cf-danger)" : "var(--cf-success)" }}>{formatEUR(treasuryEnd)}</div>
          </div>
        </div>
      </div>

      <div className="sticky bottom-24 md:static mt-6 mb-10">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="w-full py-3.5 rounded-2xl bg-[color:var(--cf-primary)] text-white font-extrabold shadow-[0_8px_20px_rgba(45,110,255,0.35)] disabled:opacity-40"
          data-testid="save-goals-btn"
        >
          {saving ? "…" : dirty ? "Enregistrer les objectifs" : "Aucun changement"}
        </button>
      </div>
    </Layout>
  );
}
